import React from 'react';
import {
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const MenuManagementPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          mb: { xs: 3, md: 5 }
        }}>
          <Box>
            <Skeleton height={isMobile ? 36 : 48} width={isMobile ? 180 : 250} style={{ marginBottom: 8 }} />
            <Skeleton height={isMobile ? 16 : 20} width={isMobile ? '100%' : 400} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Skeleton height={isMobile ? 40 : 45} width={isMobile ? 120 : 150} style={{ borderRadius: '12px' }} />
            <Skeleton height={isMobile ? 40 : 45} width={isMobile ? 120 : 150} style={{ borderRadius: '12px' }} />
          </Box>
        </Box>

        <Paper sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 3, sm: 4, md: 5 },
          borderRadius: { xs: '16px', sm: '20px' },
          border: 'none',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
            <Skeleton height={isMobile ? 24 : 32} width={isMobile ? 100 : 150} />
          </Box>

          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
            {[...Array(isMobile ? 4 : 6)].map((_, index) => (
              <Grid item xs={6} sm={6} md={4} lg={4} key={index}>
                <Card sx={{
                  borderRadius: { xs: '12px', sm: '16px', md: '20px' },
                  boxShadow: isMobile ? '0 4px 16px rgba(0, 0, 0, 0.07)' : '0 10px 30px rgba(0, 0, 0, 0.08)',
                }}>
                  <CardContent sx={{ p: { xs: 1.25, sm: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: { xs: 1, sm: 2 } }}>
                      <Skeleton height={isMobile ? 16 : 28} width={isMobile ? 70 : 120} />
                      <Skeleton height={isMobile ? 18 : 24} width={isMobile ? 40 : 60} style={{ borderRadius: '12px' }} />
                    </Box>
                    <Skeleton height={isMobile ? 12 : 16} count={isMobile ? 1 : 2} style={{ marginBottom: 4 }} />
                  </CardContent>
                  <CardActions sx={{ px: { xs: 1.25, sm: 2, md: 3 }, pb: { xs: 1.25, sm: 2, md: 3 }, pt: 0 }}>
                    <Skeleton circle width={isMobile ? 28 : 40} height={isMobile ? 28 : 40} />
                    <Skeleton circle width={isMobile ? 28 : 40} height={isMobile ? 28 : 40} />
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 3, sm: 4, md: 5 },
          borderRadius: { xs: '12px', sm: '16px', md: '20px' },
          border: 'none',
          boxShadow: isMobile ? '0 4px 16px rgba(0, 0, 0, 0.07)' : '0 10px 30px rgba(0, 0, 0, 0.08)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
            <Skeleton height={isMobile ? 24 : 32} width={isMobile ? 100 : 150} />
          </Box>

          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
            {[...Array(isMobile ? 4 : 6)].map((_, index) => (
              <Grid item xs={6} sm={6} md={4} lg={4} key={index}>
                <Card sx={{
                  borderRadius: { xs: '12px', sm: '16px', md: '20px' },
                  boxShadow: isMobile ? '0 4px 16px rgba(0, 0, 0, 0.07)' : '0 10px 30px rgba(0, 0, 0, 0.08)',
                }}>
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <Skeleton height={isMobile ? 80 : 200} />
                  </Box>

                  <CardContent sx={{ p: { xs: 1.25, sm: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: { xs: 0.5, sm: 2 } }}>
                      <Skeleton height={isMobile ? 16 : 28} width={isMobile ? 70 : 140} />
                      <Skeleton height={isMobile ? 16 : 28} width={isMobile ? 50 : 70} />
                    </Box>
                    <Skeleton height={isMobile ? 12 : 16} count={isMobile ? 1 : 2} style={{ marginBottom: 8 }} />
                  </CardContent>
                  <CardActions sx={{
                    px: { xs: 1.25, sm: 2, md: 3 },
                    pb: { xs: 1.25, sm: 2, md: 3 },
                    pt: 0,
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Skeleton circle width={isMobile ? 24 : 36} height={isMobile ? 24 : 36} />
                      <Skeleton circle width={isMobile ? 24 : 36} height={isMobile ? 24 : 36} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Skeleton circle width={isMobile ? 24 : 36} height={isMobile ? 24 : 36} />
                      <Skeleton circle width={isMobile ? 24 : 36} height={isMobile ? 24 : 36} />
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: '12px', sm: '16px', md: '20px' },
          border: 'none',
          boxShadow: isMobile ? '0 4px 16px rgba(0, 0, 0, 0.07)' : '0 10px 30px rgba(0, 0, 0, 0.08)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
            <Skeleton height={isMobile ? 24 : 32} width={isMobile ? 140 : 180} />
          </Box>

          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
            {[...Array(isMobile ? 4 : 3)].map((_, index) => (
              <Grid item xs={6} sm={6} md={4} lg={4} key={index}>
                <Card sx={{
                  borderRadius: { xs: '12px', sm: '16px', md: '20px' },
                  boxShadow: isMobile ? '0 4px 16px rgba(0, 0, 0, 0.07)' : '0 10px 30px rgba(0, 0, 0, 0.08)',
                }}>
                  <CardContent sx={{ p: { xs: 1.25, sm: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: { xs: 1, sm: 2 } }}>
                      <Skeleton height={isMobile ? 16 : 28} width={isMobile ? 70 : 130} />
                      <Skeleton height={isMobile ? 18 : 24} width={isMobile ? 40 : 60} style={{ borderRadius: '12px' }} />
                    </Box>
                    <Skeleton height={isMobile ? 12 : 16} count={isMobile ? 1 : 2} style={{ marginBottom: 12 }} />
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: 0.75, sm: 1.5 },
                      p: { xs: 1, sm: 2 },
                      borderRadius: { xs: '8px', sm: '12px' },
                      background: '#f8fafc'
                    }}>
                      <Skeleton height={isMobile ? 12 : 16} width={isMobile ? 60 : 100} />
                      <Skeleton height={isMobile ? 14 : 20} width={isMobile ? 50 : 80} />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: { xs: 1.25, sm: 2, md: 3 }, pb: { xs: 1.25, sm: 2, md: 3 }, pt: 0 }}>
                    <Skeleton circle width={isMobile ? 28 : 40} height={isMobile ? 28 : 40} />
                    <Skeleton circle width={isMobile ? 28 : 40} height={isMobile ? 28 : 40} />
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    </SkeletonTheme>
  );
};

export default MenuManagementPageSkeleton;
